import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext, useNavigate } from "react-router-dom";
import { collaboratorsAPI, projectsAPI } from "api";
import OutletWithDrawers from "global/components/router/OutletWithDrawers";
import lh from "helpers/linkHandler";
import EntitiesList, {
  Button,
  ContributorRow
} from "backend/components/list/EntitiesList";
import Authorization from "helpers/authorization";
import { useApiCallback, useFocusAfterRemoval } from "hooks";
import withConfirmation from "hoc/withConfirmation";

const authorization = new Authorization();

/* Rows are keyed by maker, not the flattened-collaborator id that `entity.id` returns. */
const getMakerId = collaborator => collaborator.relationships?.maker?.id;

function ProjectCollaboratorsContainer({ confirm }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { project, refresh } = useOutletContext() || {};

  const closeUrl = lh.link("backendProjectCollaborators", project?.id);

  const canUpdate = authorization.authorizeAbility({
    entity: project,
    ability: "updateMakers"
  });

  const destroyCollaborator = useApiCallback(collaboratorsAPI.destroy);

  const { listRef, rememberRemoval } = useFocusAfterRemoval(
    project?.relationships?.flattenedCollaborators,
    { getId: getMakerId }
  );

  const onDelete = useCallback(
    makerId => {
      const heading = t("modals.remove_contributor");
      if (confirm)
        confirm(heading, null, async () => {
          rememberRemoval(makerId);
          await destroyCollaborator("projects", project.id, { maker: makerId });
          refresh();
        });
    },
    [project?.id, confirm, destroyCollaborator, t, refresh, rememberRemoval]
  );

  const updateProject = useApiCallback(projectsAPI.update);

  const onReorder = async (_change, flattenedCollaborators) => {
    const data = flattenedCollaborators
      .map(fc =>
        fc.attributes.collaborators.map(c => ({
          id: c,
          type: "collaborators"
        }))
      )
      .flat();

    const { errors } = await updateProject(project.id, {
      attributes: {},
      relationships: { collaborators: { data } }
    });

    if (errors) {
      if (refresh) refresh();
    }
  };

  const onEdit = id => {
    navigate(lh.link("backendProjectCollaboratorEdit", project.id, id));
  };

  if (!project) return null;

  return (
    <section>
      <OutletWithDrawers
        drawerProps={{ closeUrl }}
        context={{
          refresh,
          projectId: project?.id
        }}
      />
      <EntitiesList
        className="full-width"
        wrapperRef={listRef}
        title={t("projects.contributors_header")}
        titleStyle="bar"
        titleTag="h2"
        callbacks={{ onReorder }}
        sortableStyle="tight"
        entityComponent={ContributorRow}
        entityComponentProps={canUpdate ? { onDelete, onEdit } : null}
        entities={project?.relationships?.flattenedCollaborators ?? []}
        buttons={
          canUpdate
            ? [
                <Button
                  path={lh.link("backendProjectCollaboratorNew", project.id)}
                  type="add"
                  text={t("projects.add_contributor_label")}
                />
              ]
            : []
        }
      />
    </section>
  );
}

export default withConfirmation(ProjectCollaboratorsContainer);

ProjectCollaboratorsContainer.displayName = "Project.Collaborators";
